import classNames from 'classnames'
import Link from 'next/link'
import type {HTMLAttributeAnchorTarget, MouseEventHandler} from 'react'
import {LineIcon} from '../icons/icons'
import styles from './VideoTrigger.module.scss'

/**
 * `frame` (the default): hidden at rest and revealed while its nearest
 * ancestor carrying `data-helios-video-frame` is hovered or holds focus.
 * Hidden by opacity alone, so it stays in the tab order: Tab reaches it and
 * the frame's focus-within reveals it. `always`: shown at rest (a static
 * poster).
 */
export type VideoTriggerReveal = 'frame' | 'always'

/**
 * A harness hook the trigger may carry beside its own
 * `data-helios-video-trigger`: `media-trigger` adds Media's
 * `data-helios-media-trigger` (Media sets it; the video sweep reads it).
 */
export type VideoTriggerHook = 'media-trigger'

type VideoTriggerCommonProps = {
  /** The label text, after the play glyph: the control's name. */
  children: string
  /**
   * The play glyph before the label, on by default. `false` drops it and the
   * pill's 248px minimum: a text-only pill that sizes to its label, for a
   * link over a still that is not a video (the Home hero's "Learn more").
   */
  icon?: boolean
  /** Defaults to `frame`. */
  reveal?: VideoTriggerReveal
  hook?: VideoTriggerHook
}

type VideoTriggerButtonProps = VideoTriggerCommonProps & {
  /** Renders a `<button type="button">`. */
  onClick: MouseEventHandler<HTMLButtonElement>
  href?: never
  clientNavigation?: never
  target?: never
  rel?: never
}

type VideoTriggerLinkProps = VideoTriggerCommonProps & {
  /** Renders a link, as ButtonLink does. */
  href: string
  /**
   * `false` renders a plain `<a>` (a document navigation, e.g. another
   * site); otherwise a Next.js `<Link>`. Defaults to `true`.
   */
  clientNavigation?: boolean
  target?: HTMLAttributeAnchorTarget
  rel?: string
  onClick?: never
}

export type VideoTriggerProps = VideoTriggerButtonProps | VideoTriggerLinkProps

/**
 * The dark translucent pill over a video or a poster that opens the video
 * (Phase 5: the legacy `.video-action-btn` look, O221 and O298): the
 * `media-play` glyph (unless `icon={false}`) and white subhead text on
 * `surface-video-trigger`, with the `focus-ring-on-media` ring (the brand
 * ring on a white halo, O190). A button with `onClick`, or a link with
 * `href`: the pill itself is the control, whatever surrounds it. Positioning
 * is the consumer's: a wrapper places it over the media and marks the
 * element whose hover reveals it with `data-helios-video-frame`.
 * Presentational: no analytics, no className, no style.
 */
function VideoTrigger(props: VideoTriggerProps) {
  const {children, hook, icon = true, reveal = 'frame'} = props
  const className = classNames(
    styles['helios-video-trigger'],
    !icon && styles['helios-video-trigger--text-only'],
    reveal === 'frame' && styles['helios-video-trigger--reveal-frame'],
  )
  const hooks = {
    'data-helios-video-trigger': reveal,
    'data-helios-media-trigger': hook === 'media-trigger' ? '' : undefined,
  }
  const content = (
    <>
      {icon && (
        <LineIcon
          className={styles['helios-video-trigger__icon']}
          name="media-play"
        />
      )}
      {children}
    </>
  )

  if (props.href !== undefined) {
    const {clientNavigation = true, href, rel, target} = props
    if (!clientNavigation) {
      return (
        <a
          className={className}
          href={href}
          rel={rel}
          target={target}
          {...hooks}
        >
          {content}
        </a>
      )
    }
    return (
      <Link
        className={className}
        href={href}
        rel={rel}
        target={target}
        {...hooks}
      >
        {content}
      </Link>
    )
  }

  return (
    <button
      className={className}
      onClick={props.onClick}
      type="button"
      {...hooks}
    >
      {content}
    </button>
  )
}

export default VideoTrigger
